import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

interface AdminRouteGuardProps {
  children: React.ReactNode;
}

const AdminRouteGuard: React.FC<AdminRouteGuardProps> = ({ children }) => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // While the AuthProvider is determining session status, do not redirect.
    if (loading) return;

    const userActiveRoles = (user as any)?.active_roles || [];
    const userType = String((user as any)?.user_type || '').toLowerCase();

    // If there is no user, redirect to admin login
    if (!user) {
      console.log('[ADMIN_GUARD] No user found - redirecting to admin login');
      navigate('/admin/login', { replace: true, state: { from: window.location.pathname } });
      return;
    }

    // STRICT: Only allow users with explicit admin role or user_type='admin'
    // Reject all other user types (buyer, seller, agent, etc.)
    const hasAdminAccess = userActiveRoles.includes('admin') || 
                          (userActiveRoles.length === 0 && userType === 'admin');

    if (!hasAdminAccess) {
      console.log(`[ADMIN_GUARD] Access DENIED. User roles: [${userActiveRoles.join(', ')}], User type: '${userType}'. Redirecting to appropriate dashboard.`);
      
      // Redirect to appropriate dashboard based on user type instead of admin login
      if (userType === 'buyer') {
        navigate('/buy', { replace: true });
      } else if (userType === 'seller') {
        navigate('/seller/dashboard', { replace: true });
      } else if (userType === 'agent') {
        navigate('/agent/dashboard', { replace: true });
      } else {
        // For unknown types, redirect to homepage
        navigate('/', { replace: true });
      }
      return;
    }

    console.log(`[ADMIN_GUARD] Access GRANTED. User roles: [${userActiveRoles.join(', ')}], User type: '${userType}'`);
  }, [user, loading, navigate]);

  // Show loading while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#061D58] via-[#0A2351] to-[#163169] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto"></div>
          <p className="mt-4 text-white">Verifying admin access...</p>
        </div>
      </div>
    );
  }

  // If not authenticated or not admin, don't render children
  const userActiveRoles = (user as any)?.active_roles || [];
  const userType = String((user as any)?.user_type || '').toLowerCase();
  
  const hasAdminAccess = userActiveRoles.includes('admin') || 
                        (userActiveRoles.length === 0 && userType === 'admin');

  if (!user || !hasAdminAccess) {
    // Return null while redirect is happening
    return null;
  }

  return <>{children}</>;
};

export default AdminRouteGuard;
